import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, ShieldCheck, Crown, Clock, Zap, Building2, User } from "lucide-react";
import { BadgeRow } from "@/components/AchievementBadges";
import { DbsPill } from "@/components/DbsStatusPill";

const SERVICE_LABEL = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CleanerCard({ c, bookContext }) {
  const navigate = useNavigate();

  const bookUrl = () => {
    const p = new URLSearchParams();
    if (bookContext?.service) p.set("service", bookContext.service);
    if (bookContext?.date) p.set("date", bookContext.date);
    if (bookContext?.slot) p.set("slot", bookContext.slot);
    if (bookContext?.property) p.set("property", bookContext.property);
    const qs = p.toString();
    return `/book/${c.cleaner_id}${qs ? `?${qs}` : ""}`;
  };

  return (
    <div className="bg-card rounded-2xl border border-primary/5 shadow-[0_8px_32px_rgba(8,90,140,0.04)] overflow-hidden hover-lift flex flex-col" data-testid={`cleaner-card-${c.cleaner_id}`}>
      <div className="relative h-44">
        {c.photo_url
          ? <img src={c.photo_url} alt={c.name} className="h-full w-full object-cover bg-secondary" loading="lazy" />
          : <div className="h-full w-full bg-secondary flex items-center justify-center text-2xl font-display font-semibold text-muted-foreground" data-testid="cleaner-photo-placeholder">{(c.name || "?").charAt(0)}</div>}
        {c.premium && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-white text-xs font-semibold" data-testid="premium-badge">
            <Crown className="h-3.5 w-3.5" /> Premium
          </span>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-primary text-xs font-semibold">
          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {c.rating_avg || "New"}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-semibold leading-tight">{c.name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="h-3.5 w-3.5" /> {c.town}
            </p>
            <span className={`inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.type === "company" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}
              data-testid={`provider-type-${c.cleaner_id}`}>
              {c.type === "company" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
              {c.provider_label || (c.type === "company" ? "Cleaning Company" : "Independent Cleaner")}
            </span>
          </div>
          <div className="text-right shrink-0">
            <p className="font-display text-xl font-semibold">£{c.hourly_rate}</p>
            <p className="text-xs text-muted-foreground">/hour</p>
            {c.priority_enabled && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid={`priority-label-${c.cleaner_id}`}>
                <Zap className="h-3 w-3" /> Priority £{c.priority_hourly_rate || c.hourly_rate}
              </span>
            )}
          </div>
        </div>

        {c.bio && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.bio}</p>}

        <div className="mt-3"><BadgeRow earned={(c.badges || []).filter((b) => b !== "dbs_checked")} max={2} /></div>
        <div className="mt-2"><DbsPill dbs={c.dbs} /></div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {(c.services || []).slice(0, 3).map((s) => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-secondary text-xs">{SERVICE_LABEL(s)}</span>
          ))}
          {c.services?.length > 3 && <span className="px-2 py-0.5 rounded-full bg-secondary text-xs">+{c.services.length - 3}</span>}
        </div>

        <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {c.rating_avg || "New"} ({c.review_count})</span>
          <span>{c.completed_jobs} cleans</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.years_experience}y exp</span>
        </div>

        <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border">
          <Link to={`/cleaners/${c.cleaner_id}`} className="flex-1 text-center px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`view-cleaner-${c.cleaner_id}`}>
            View
          </Link>
          <button onClick={() => navigate(bookUrl())} className="flex-1 px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors" data-testid={`book-cleaner-${c.cleaner_id}`}>
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}
